// src/pages/admin/components/Users.jsx
import React, { useState } from 'react';
import SkeletonAdmin from '../../skeleton/skeletonAdmin';
import { getAvatarColor } from '../../../pages/admin/getAvatarColor';
import StudentDetailsModal from './StudentDetailsModal';
import { Characters } from '../../users/account/characterCollection';

const Users = ({
  users,
  loading,
  filteredUsers,
  search,
  setSearch,
  roleFilter,
  setRoleFilter,
  updateUserStatus, // Changed from blockUser
  blocking
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false); // Renamed from showBlockModal
  const [selectedUser, setSelectedUser] = useState(null);
  const [reason, setReason] = useState("");
  const [statusTab, setStatusTab] = useState('active');
  const [actionType, setActionType] = useState('block'); // 'block' or 'unblock'

  const handleStatusUpdate = async () => {
    const payload = {
      is_active: actionType === 'unblock', // true for unblock, false for block
      ...(actionType === 'block' && { reason }) // Only include reason when blocking
    };

    const success = await updateUserStatus(selectedUser.id, payload);

    if (success) {
      setShowStatusModal(false);
      setReason("");
      setSelectedUser(null);
      setActionType('block');
    }
  };

  // Filter users based on status tab
  // Filter users based on status tab
  const tabFilteredUsers = filteredUsers.filter(u => {
    if (statusTab === 'deleted') return u.is_deleted;
    if (statusTab === 'active') return u.is_active && !u.is_deleted;
    if (statusTab === 'blocked') return !u.is_active && !u.is_deleted;
    return true;
  });

  // Get counts for tabs
  const activeCount = filteredUsers.filter(u => u.is_active).length;
  const blockedCount = filteredUsers.filter(u => !u.is_active).length;
  const deletedCount = filteredUsers.filter(u => u.is_deleted).length;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Users</h2>

      {/* Search and Role Filter */}
      <div className="flex items-center gap-2 w-full text-sm">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="border p-2 rounded-md flex-1 min-w-0"
        />

        {/* <select
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
          className="border p-2 rounded-md w-[100px] sm:w-auto"
        >
          <option value="ALL">All</option>
          <option value="STUDENT">Student</option>
          <option value="LAND_OWNER">Landowner</option>
        </select> */}
      </div>

      {/* Status Tabs */}
      <div className="flex border-b overflow-x-auto scrollbar-hide">
        <button
          onClick={() => setStatusTab('active')}
          className={`relative flex-shrink-0 px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${statusTab === 'active'
            ? 'text-green-600 border-b-2 border-green-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          <div className="flex items-center gap-1.5">
            <span>Active</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusTab === 'active'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-600'
              }`}>
              {activeCount}
            </span>
          </div>
        </button>

        <button
          onClick={() => setStatusTab('blocked')}
          className={`relative flex-shrink-0 px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${statusTab === 'blocked'
            ? 'text-red-600 border-b-2 border-red-600'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          <div className="flex items-center gap-1.5">
            <span>Blocked</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusTab === 'blocked'
              ? 'bg-red-100 text-red-700'
              : 'bg-gray-100 text-gray-600'
              }`}>
              {blockedCount}
            </span>
          </div>
        </button>

        <button
          onClick={() => setStatusTab('deleted')}
          className={`relative flex-shrink-0 px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${statusTab === 'deleted'
            ? 'text-gray-900 border-b-2 border-gray-900'
            : 'text-gray-500 hover:text-gray-700'
            }`}
        >
          <div className="flex items-center gap-1.5">
            <span>Deleted</span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusTab === 'deleted'
              ? 'bg-gray-200 text-gray-800'
              : 'bg-gray-100 text-gray-600'
              }`}>
              {deletedCount}
            </span>
          </div>
        </button>
      </div>

      {/* Users List */}
      {loading ? (
        <SkeletonAdmin />
      ) : tabFilteredUsers.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No {statusTab} users found
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {tabFilteredUsers.map(u => {
            const character = Characters.find((c) => String(c.id) === String(u.avatar_id));

            return (
              <div
                key={u.id}
                className="relative border p-2 rounded-2xl flex gap-2 sm:gap-3 items-center cursor-pointer hover:bg-gray-50 transition"
                onClick={() => {
                  setSelectedUser(u);
                  setShowDetails(true);
                }}
              >
                {/* Avatar */}
                <div
                  className={`w-10 h-10 sm:w-14 sm:h-14 flex-shrink-0 rounded-xl flex items-center justify-center overflow-hidden text-base sm:text-lg font-semibold text-black ${u.avatar ? "bg-gray-200" : getAvatarColor(u.full_name)
                    }`}
                >
                  {u.avatar_id && character ? (
                    <img
                      src={character.img}
                      alt={u.full_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    u.full_name?.charAt(0).toUpperCase()
                  )}
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm sm:text-base truncate">{u.full_name}</p>
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{u.email}</p>
                </div>

                {/* Status Badge */}
                <span
                  className={`flex-shrink-0 px-2 sm:px-3 py-1 text-xs rounded-2xl ${u.is_deleted
                      ? "bg-gray-100 text-gray-600"
                      : u.is_active
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                >
                  {u.is_deleted ? "Deleted" : u.is_active ? "Active" : "Blocked"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showDetails && selectedUser && (
        <StudentDetailsModal
          id={selectedUser.id}
          avatar_id={selectedUser.avatar_id}
          role={selectedUser.role}
          is_active={selectedUser.is_active}
          onClose={() => {
            setShowDetails(false);
            setSelectedUser(null);
          }}
          onStatusUpdate={(type) => {
            setActionType(type);
            setShowStatusModal(true);
          }}
        />
      )}

      {/* Status Update Modal (Block/Unblock) */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[90%] max-w-md space-y-4">
            <h3 className="text-lg font-semibold">
              {actionType === 'block' ? 'Block User' : 'Unblock User'}
            </h3>
            <p className="text-sm text-gray-500">{selectedUser?.full_name}</p>

            {actionType === 'block' && (
              <>
                <p className="text-sm text-gray-600">
                  Please provide a reason for blocking this user:
                </p>
                <textarea
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Enter reason..."
                  className="w-full border rounded-xl p-3 text-sm resize-none"
                  rows={4}
                />
              </>
            )}

            {actionType === 'unblock' && (
              <p className="text-sm text-gray-600">
                Are you sure you want to unblock this user? They will be able to access their account again.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setReason("");
                  setSelectedUser(null);
                  setActionType('block');
                }}
                className="px-4 py-2 rounded-xl border hover:bg-gray-50 transition"
              >
                Cancel
              </button>

              <button
                disabled={blocking || (actionType === 'block' && !reason)}
                onClick={handleStatusUpdate}
                className={`px-4 py-2 rounded-xl text-white disabled:opacity-50 transition ${actionType === 'block'
                  ? 'bg-red-500 hover:bg-red-600'
                  : 'bg-green-500 hover:bg-green-600'
                  }`}
              >
                {blocking
                  ? (actionType === 'block' ? 'Blocking...' : 'Unblocking...')
                  : (actionType === 'block' ? 'Block User' : 'Unblock User')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;